if (!customElements.get('pickup-availability')) {
  customElements.define('pickup-availability', class PickupAvailability extends HTMLElement {
    constructor() {
      super();
      if (!this.hasAttribute('available')) return;
      this.onClickRefreshList = this.onClickRefreshList.bind(this);
      this.fetchAvailability(this.dataset.variantId);
    }

    fetchAvailability(variantId) {
      let rootUrl = this.dataset.rootUrl;
      if (!rootUrl.endsWith('/')) rootUrl += '/';

      const sectionUrl = `${rootUrl}variants/${variantId}/?section_id=pickup-availability`;
      const workerUrl = this.dataset.geoWorkerUrl;

      // Always fetch the Shopify section (accurate pickup times + pick_up_enabled filtering).
      // If a worker URL is configured, also fetch geo data and sort by distance.
      const sectionFetch = fetch(sectionUrl).then(r => r.text());
      const geoFetch = workerUrl
        ? fetch(`${workerUrl}/pickup?variantId=${variantId}`)
            .then(r => r.ok ? r.json() : null)
            .catch(() => null)
        : Promise.resolve(null);

      Promise.all([sectionFetch, geoFetch])
        .then(([sectionText, geoData]) => {
          const doc = new DOMParser().parseFromString(sectionText, 'text/html');
          const sectionEl = doc.querySelector('.shopify-section');

          if (geoData && geoData.userLocation?.available && geoData.stores?.length > 1) {
            this.applyGeoSort(sectionEl, geoData);
          }

          this.renderPreview(sectionEl);
        })
        .catch(() => {
          const button = this.querySelector('button');
          if (button) button.removeEventListener('click', this.onClickRefreshList);
          this.renderError();
        });
    }

    /**
     * Reorders the drawer list by geo distance and updates the inline summary
     * to reflect the closest store. All pickup time text is preserved from
     * Shopify's server-rendered HTML — we only change order and add distance labels.
     */
    applyGeoSort(sectionEl, geoData) {
      if (!sectionEl) return;

      // Build a name → store lookup from the worker response
      const storeByName = {};
      for (const store of geoData.stores) {
        storeByName[store.name] = store;
      }

      // ── Sort the drawer list ─────────────────────────────────────────────
      const list = sectionEl.querySelector('.pickup-availability-list');
      if (list) {
        const items = Array.from(list.querySelectorAll('li.pickup-availability-list__item'));

        items.sort((a, b) => {
          const aName = a.querySelector('.pickup-availability-list__location')?.textContent.trim() ?? '';
          const bName = b.querySelector('.pickup-availability-list__location')?.textContent.trim() ?? '';
          const aStore = storeByName[aName];
          const bStore = storeByName[bName];
          // Available stores first, then by distance
          const aAvail = aStore?.available ?? true;
          const bAvail = bStore?.available ?? true;
          if (aAvail !== bAvail) return bAvail - aAvail;
          return (aStore?.distanceMi ?? Infinity) - (bStore?.distanceMi ?? Infinity);
        });

        // Re-insert in sorted order and add distance label
        list.innerHTML = '';
        for (const item of items) {
          const name = item.querySelector('.pickup-availability-list__location')?.textContent.trim() ?? '';
          const store = storeByName[name];
          if (store?.distanceMi != null) {
            const distEl = document.createElement('p');
            distEl.className = 'pickup-availability-small-text pickup-availability-distance';
            distEl.textContent = `${store.distanceMi} mi away`;
            item.appendChild(distEl);
          }
          list.appendChild(item);
        }

        // ── Update inline summary to show the geo-closest store ─────────────
        // The server rendered the summary for Shopify's first store; if that
        // differs from the geo-closest, patch just the store name and availability.
        const closestItem = list.querySelector('li.pickup-availability-list__item');
        if (closestItem) {
          this.updateInlineSummary(sectionEl, closestItem, storeByName);
        }
      }
    }

    updateInlineSummary(sectionEl, closestItem, storeByName) {
      const summary = sectionEl.querySelector('.pickup-availability-information');
      if (!summary) return;

      const closestName = closestItem
        .querySelector('.pickup-availability-list__location')
        ?.textContent.trim() ?? '';
      const closestStore = storeByName[closestName];
      if (!closestStore) return;

      // Skip if the server already rendered this exact store first
      const currentNameEl = summary.querySelector('.pickup-availability-information__title span, .pickup-availability-information__title strong');
      if (currentNameEl?.textContent.trim() === closestName) return;

      const container = summary.querySelector('.pickup-availability-information-container');
      if (!container) return;

      const wasUnavailable = !container.classList.contains('is-available');
      const willBeAvailable = closestStore.available;

      // Rebuild the title text (available/unavailable use different Liquid branches)
      const titleEl = summary.querySelector('.pickup-availability-information__title');
      if (titleEl) {
        titleEl.innerHTML = willBeAvailable
          ? `Pickup available at <span>${closestName}</span>`
          : `Pickup currently unavailable at <span>${closestName}</span>`;
      }

      // The available and unavailable states have different DOM structures:
      // Available:   .pickup-space-holder > (title + button), then .stock below
      // Unavailable: title and button are direct children, no .stock
      // Restructure the DOM when the state changes.
      if (wasUnavailable && willBeAvailable) {
        const buttonEl = container.querySelector('.pickup-availability-information__button');
        if (titleEl && buttonEl && !container.querySelector('.pickup-space-holder')) {
          const spaceHolder = document.createElement('div');
          spaceHolder.className = 'pickup-space-holder';
          container.insertBefore(spaceHolder, titleEl);
          spaceHolder.appendChild(titleEl);
          spaceHolder.appendChild(buttonEl);
        }
        if (!container.querySelector('.pickup-availability-information__stock')) {
          const stockEl = document.createElement('p');
          stockEl.className = 'pickup-availability-information__stock';
          container.appendChild(stockEl);
        }
      } else if (!wasUnavailable && !willBeAvailable) {
        const spaceHolder = container.querySelector('.pickup-space-holder');
        if (spaceHolder) {
          while (spaceHolder.firstChild) container.insertBefore(spaceHolder.firstChild, spaceHolder);
          spaceHolder.remove();
        }
        container.querySelector('.pickup-availability-information__stock')?.remove();
      }

      // Sync availability class
      container.classList.toggle('is-available', willBeAvailable);

      // Swap icon to checkmark when available
      const iconContainer = summary.querySelector('svg, .icon');
      if (iconContainer && willBeAvailable) {
        iconContainer.outerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M21.5307 7.28062L9.53068 19.2806C9.46102 19.3504 9.3783 19.4057 9.28726 19.4434C9.19621 19.4812 9.09861 19.5006 9.00005 19.5006C8.90149 19.5006 8.80389 19.4812 8.71285 19.4434C8.6218 19.4057 8.53908 19.3504 8.46943 19.2806L3.21943 14.0306C3.0787 13.8899 2.99963 13.699 2.99963 13.5C2.99963 13.301 3.0787 13.1101 3.21943 12.9694C3.36016 12.8286 3.55103 12.7496 3.75005 12.7496C3.94907 12.7496 4.13995 12.8286 4.28068 12.9694L9.00005 17.6897L20.4694 6.21937C20.6102 6.07864 20.801 5.99958 21.0001 5.99958C21.1991 5.99958 21.3899 6.07864 21.5307 6.21937C21.6714 6.3601 21.7505 6.55097 21.7505 6.75C21.7505 6.94902 21.6714 7.13989 21.5307 7.28062Z" fill="black"/>
        </svg>`;
      }

      // Update pickup time from the closest drawer item (text after the first comma)
      if (willBeAvailable) {
        const drawerStock = closestItem.querySelector('.pickup-availability-list__stock');
        const summaryStock = container.querySelector('.pickup-availability-information__stock');
        if (drawerStock && summaryStock) {
          const stockText = drawerStock.textContent.trim();
          const commaIdx = stockText.indexOf(',');
          if (commaIdx > -1) summaryStock.textContent = stockText.slice(commaIdx + 1).trim();
        }
      }
    }

    onClickRefreshList() {
      this.fetchAvailability(this.dataset.variantId);
    }

    renderError() {
      this.innerHTML = '';
      const button = this.querySelector('button');
      if (button) button.addEventListener('click', this.onClickRefreshList);
    }

    renderPreview(sectionInnerHTML) {
      const drawer = document.querySelector('.pickup-availability-drawer');
      if (drawer) drawer.remove();

      if (!sectionInnerHTML?.querySelector('.pickup-availability-information')) {
        this.innerHTML = '';
        this.removeAttribute('available');
        return;
      }

      this.innerHTML = sectionInnerHTML.querySelector('.pickup-availability-information').innerHTML;
      this.setAttribute('available', '');

      document.getElementById('Pickup-Availability').innerHTML =
        sectionInnerHTML.querySelector('.side-availability').innerHTML;

      const button = this.querySelector('button');
      if (button) {
        button.addEventListener('click', evt => {
          document.getElementById('Pickup-Availability').classList.toggle('active');
          document.getElementsByTagName('body')[0].classList.toggle('open-cc');
          evt.preventDefault();
        });
      }
    }
  });
}
